import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import useUiuxStore from "src/store/useUiuxStore";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";

import { GridCalendar } from "src/components/common/form/GridCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, getSwitchButtonColumnOptions, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { SYS4030_R01Dto } from "./Dto_SYS4030_R01";
import { useSYS4030_R01Store, useSYS4030_R01Store_GridCalendar } from "./Store_SYS4030_R01";
import { CommonCodeColumn, CompCdColumn, getCommonCodeColumnOptions, getCompCdColumnOptions } from "src/utils/grid/GridColumnContent";
import { showWarning } from "src/utils/toast/toastUtil";
import { headerNames_SYS4030_R01 } from "./Util_SYS4030_R01";
import { useSYS4030_R01CellChange } from "./hooks/SYS4030_R01CellChange";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";


const COLUMN_STATE_KEY = 'SYS4030_R01-grid-column-state';

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<SYS4030_R01Dto> | null>;
	defaultSYS4030_R01Row: SYS4030_R01Dto;
}

export const SYS4030_R01Table = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultSYS4030_R01Row } = props;

	const {
		SYS4030_R01List, setSYS4030_R01List,
		insertSYS4030_R01List, removeSYS4030_R01List, updateSYS4030_R01List,
		deletedSYS4030_R01List, addDeletedSYS4030_R01List,
		selectedSYS4030_R01List, addSelectedSYS4030_R01List, removeSelectedSYS4030_R01List,
		initSelectedSYS4030_R01List, allSelectedSYS4030_R01List,
		originalSYS4030_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSYS4030_R01Store();
	const { SYS4030_R01GridCalendarState, setSYS4030_R01GridCalendarState } = useSYS4030_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = SYS4030_R01GridCalendarState;
	const { compCdList, wmsCdList, inoutBoundList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS4030_R01List,
		defaultGridData: defaultSYS4030_R01Row,
		selectedRow: selectedSYS4030_R01List,
		originalGridData: originalSYS4030_R01List,
		updateRow: (index: number, data: any) => updateSYS4030_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['customNm', ],
		insertedDisabledCells: [''],
		commonDisabledCells: ['warehouseCd', ],
		
		pointerCells: ['warehouseNm', 'displaySeq', 'inoutType', 'logicCenter', 'compCd', 'customCd', 'customNm', 'materialYn', 'goodsYn', 'productYn', 'manufactYn', 'badYn', 'fifiYn', 'useYn', 'approveFlg', 'remark'],
		insertedPointerCells: ['warehouseCd', ],
		commonPointerCells: [''],
		
		alignLeftCells: ['warehouseCd', 'compCd']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<SYS4030_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedSYS4030_R01List,
            deletedList: deletedSYS4030_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [selectedSYS4030_R01List, deletedSYS4030_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'warehouseCd', 'warehouseNm', 'displaySeq', 'inoutType', 'logicCenter', 'compCd', 'customCd', 'customNm', 'materialYn', 'goodsYn', 'productYn', 'manufactYn', 'badYn', 'fifiYn', 'useYn', 'approveFlg', 'remark' ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['warehouseCd', 'warehouseNm', 'displaySeq', 'inoutType', 'logicCenter', 'compCd', 'customCd', 'customNm', 'remark'];


	const settingCodeSearchModalProps = (
		category: string,
		search: string,
		isInfo: boolean,
		handleFoucsId: string,
		originalData: SYS4030_R01Dto,
		valueIndex: number
	) => {
		setCodeSearchModalProps({
			category,
			search,
			formName: 'SYS4030_R01',
			isInfo,
			handleFoucsId, // 코드 검색 후 입력 포커스 이동을 위한 아이디
			onClear: () => {
				updateSYS4030_R01List(valueIndex, { ...originalData, customCd: '', customNm: '', isModified: originalData.inserted ? false : true })
			},
			onConfirm: (data: any) => {
				updateSYS4030_R01List(valueIndex, {
					...originalData,
					customCd: data.customCd,
					customNm: data.customNm,
					isModified: originalData.inserted ? false : true
				})
			}
		})	
	}

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: SYS4030_R01List,
			rowData: SYS4030_R01List[rowIndex],
			insertRow: insertSYS4030_R01List,
			defaultRowData: defaultSYS4030_R01Row,
			setClickable
		});
	}, [SYS4030_R01List, insertSYS4030_R01List, defaultSYS4030_R01Row, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<SYS4030_R01Dto>(defaultEditableColumns)
	, [defaultEditableColumns]);

	const baseColumnDefs: ColDef<SYS4030_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SYS4030_R01Dto) => renderIndexCell(
				params, 
				(innerParams: SYS4030_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSYS4030_R01List,
						selectedList: selectedSYS4030_R01List,
						dataList: SYS4030_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SYS4030_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeSYS4030_R01List, 
					(valueIndex) => addDeletedSYS4030_R01List([valueIndex.toString()])
				),
			)
		),
        {
            headerName: '창고번호', colId: 'warehouseCd', field: 'warehouseCd', width: 100,
			editable: (params) => { return params.data?.inserted === true? true : false; }, // 해당 행이 추가된(신규) 행일 때만 편집 가능
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 110, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		{
            headerName: '조회번호', colId: 'displaySeq', field: 'displaySeq', width: 80,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
		{
            headerName: '내외부구분', colId: 'inoutType', field: 'inoutType', width: 100,
			...CommonCodeColumn.options(inoutBoundList, true, CommonCodeColumn.change(updateSYS4030_R01List), handleGridDropDownCommitAfter),
        },
		{
            headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 100,
			...CommonCodeColumn.options(wmsCdList, true, CommonCodeColumn.change(updateSYS4030_R01List), handleGridDropDownCommitAfter),
        },
		// 에러를 일으키는 코드
		{
            headerName: '사업자명', colId: 'compCd', field: 'compCd', width: 100, 
			...CompCdColumn.options(compCdList, true, CompCdColumn.change(updateSYS4030_R01List), handleGridDropDownCommitAfter),
        },
		{
            headerName: '거래처코드', colId: 'customCd', field: 'customCd', width: 100, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		{
            headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 100, 
			editable: false,
        },
		{
            headerName: '자재창고사용', colId: 'materialYn', field: 'materialYn', width: 100, 
			...getSwitchButtonColumnOptions('materialYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '상품창고사용', colId: 'goodsYn', field: 'goodsYn', width: 100, 
			...getSwitchButtonColumnOptions('goodsYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '제품창고사용', colId: 'productYn', field: 'productYn', width: 100, 
			...getSwitchButtonColumnOptions('productYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '공정창고사용', colId: 'manufactYn', field: 'manufactYn', width: 100, 
			...getSwitchButtonColumnOptions('manufactYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '불량창고사용', colId: 'badYn', field: 'badYn', width: 100, 
			...getSwitchButtonColumnOptions('badYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '선입선출', colId: 'fifiYn', field: 'fifiYn', width: 100, 
			...getSwitchButtonColumnOptions('fifiYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '사용여부', colId: 'useYn', field: 'useYn', width: 100, 
			...getSwitchButtonColumnOptions('useYn', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '승인여부', colId: 'approveFlg', field: 'approveFlg', width: 100, 
			...getSwitchButtonColumnOptions('approveFlg', handleSwitchButtonHandler, headerNames_SYS4030_R01, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 110, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		// ...IndexParameterCell(),
	], [deletedSYS4030_R01List, SYS4030_R01List, createCellClassRules]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS4030_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SYS4030_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_SYS4030_R01,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

    
	//cellChange 설정
	const { handleCellChange, handleCustomCdSearch } = useSYS4030_R01CellChange({
		SYS4030_R01List,
		updateSYS4030_R01List,
		originalSYS4030_R01List,
		settingCodeSearchModalProps,
		gridRef,
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: SYS4030_R01List,
        idKey: 'valueIndex',
        selectedList: selectedSYS4030_R01List,
        deletedList: deletedSYS4030_R01List,
        addSelected: addSelectedSYS4030_R01List,
        removeSelected: removeSelectedSYS4030_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS4030_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<SYS4030_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [settingCodeSearchModalProps]);
	const handleCellClick = CreateCellClickHandler(SYS4030_R01List, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS4030_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'customCd' || field === 'customNm',
			action: (e, foundData) => {
				if(foundData.inoutType?.includes('IN')) {
					showWarning('내부창고에는 거래처명을 입력할 수 없습니다.');
				} else {
                    // 수정: 두 번째 인자에 빈 문자열 대신 foundData.customCd 값을 전달
					settingCodeSearchModalProps('거래처', foundData.customCd || '', true, 'customer', foundData, foundData.valueIndex!);
				}
			}
		}

	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(SYS4030_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS4030_R01Dto>) => {
		postSortHandler(params, SYS4030_R01List, originalSYS4030_R01List, setSYS4030_R01List);
	}, [SYS4030_R01List, originalSYS4030_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		warehouseCd: SYS4030_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [SYS4030_R01List]);

    return (
		<GridContextWrapper<SYS4030_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSYS4030_R01List}
			initSelected={initSelectedSYS4030_R01List}	
			defaultRowData={defaultSYS4030_R01Row}
			allSelected={allSelectedSYS4030_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SYS4030_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<SYS4030_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS4030_R01List,
					deletedList: deletedSYS4030_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>

			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setSYS4030_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) => setSYS4030_R01GridCalendarState({ pickerPosition: position })}
				onClear={() => {
					setSYS4030_R01GridCalendarState({ targetDate: undefined });
				}}
				onChange={(date, rowIndex, targetField) => {
					if (rowIndex !== null && targetField) {
						const foundData = SYS4030_R01List[rowIndex];
						if (foundData) {
							updateSYS4030_R01List(foundData.valueIndex!, {
								...foundData,
								[targetField]: date ? formatDate(date) : '',
								isModified: true // 날짜 변경 시 수정 상태로 설정
							});
						}
					}
				}}
				onClickLoad={(rowIndex, targetField) => {
					// 필요시 추가 로직
					const foundData = SYS4030_R01List[rowIndex];
					const dateString = foundData[targetField as keyof SYS4030_R01Dto] as string;
					if(dateString !== '' && dateString !== null) {
						setSYS4030_R01GridCalendarState({ targetDate: new Date(dateString) });
					}else{
						setSYS4030_R01GridCalendarState({ targetDate: new Date() });
					}
				}}
			/>
		</GridContextWrapper>
    )
}
